import { checkData, getData } from "../reducer/helper";

/**
 * Core GraphQL HTTP endpoint (`meetoor_core` / `core_api`).
 * Development default matches local backend (never production URL).
 *
 * Priority:
 * - REACT_APP_CORE_GRAPHQL_URL (full URL to endpoint, trailing slash optional)
 * - REACT_APP_API_BASE_URL + `/core/graphql/` (expects base ending with `/api`, same as Axios)
 * - http://127.0.0.1:8000/api/core/graphql/ when NODE_ENV === development
 */
export function getCoreGraphqlEndpoint() {
    const explicit = (process.env.REACT_APP_CORE_GRAPHQL_URL || "").trim();
    if (explicit) {
        return explicit.replace(/\/+$/, "") + "/";
    }
    const apiBase = (process.env.REACT_APP_API_BASE_URL || "").trim();
    if (apiBase) {
        const api = apiBase.replace(/\/+$/, "");
        return `${api}/core/graphql/`;
    }
    if (process.env.NODE_ENV === "development") {
        return "http://127.0.0.1:8000/api/core/graphql/";
    }
    try {
        return new URL(window.location.origin).origin.replace(/\/+$/, "") + "/api/core/graphql/";
    } catch {
        return "http://127.0.0.1:8000/api/core/graphql/";
    }
}

/** @deprecated alias — prefer `getCoreAuthToken`. */
export const getAuthToken = getCoreAuthToken;

/**
 * @param {string|null|undefined} token explicit token, or read from localStorage (same key as REST)
 * @returns {string|null}
 */
export function getCoreAuthToken(token) {
    let auth = token;
    if (auth === undefined) {
        auth = null;
        if (checkData("token")) {
            try {
                auth = getData("token");
            } catch {
                auth = null;
            }
        }
    }
    return auth || null;
}

/** In-flight query dedupe (mutations are never deduped). */
const inFlightGraphqlRequests = new Map();

/** @param {string} query */
function getGraphqlOperationName(query) {
    const match = query.match(/\b(query|mutation)\s+([A-Za-z0-9_]+)/);
    return match?.[2] || "anonymous";
}

/** @param {string} query */
function isGraphqlMutation(query) {
    return /^\s*mutation\b/i.test(query);
}

/**
 * POST JSON to Core GraphQL. Throws on HTTP failure, invalid JSON, or GraphQL top-level errors.
 * Identical in-flight **queries** (same operation name, variables, token) share one Promise.
 *
 * @param {string} query
 * @param {Record<string, unknown>} [variables]
 * @param {string|null|undefined} [token]
 * @param {RequestInit} [init] e.g. `{ signal }` for AbortController
 * @returns {Promise<Record<string, unknown>>} response `data` object
 */
export async function coreGraphql(query, variables = {}, token, init = {}) {
    const operationName = getGraphqlOperationName(query);
    const authKey = getCoreAuthToken(token) || "";
    const normVars = variables && typeof variables === "object" ? { ...variables } : {};
    const dedupeKey = JSON.stringify({
        operationName,
        variables: normVars,
        token: authKey,
    });

    if (!isGraphqlMutation(query) && inFlightGraphqlRequests.has(dedupeKey)) {
        if (process.env.NODE_ENV === "development") {
            console.warn("[CoreGraphQL duplicate prevented]", operationName, normVars);
        }
        return inFlightGraphqlRequests.get(dedupeKey);
    }

    const requestPromise = (async () => {
        const auth = getCoreAuthToken(token);
        const headers = { "Content-Type": "application/json" };
        if (auth) {
            headers.Authorization = `Token ${auth}`;
        }
        const endpoint = getCoreGraphqlEndpoint();
        const payload = {
            method: "POST",
            headers,
            body: JSON.stringify({ query, variables }),
            ...init,
        };
        let res;
        try {
            res = await fetch(endpoint, payload);
        } catch (e) {
            const fallbackEndpoint = "http://127.0.0.1:8000/api/core/graphql/";
            const explicit = (process.env.REACT_APP_CORE_GRAPHQL_URL || "").trim();
            const apiBase = (process.env.REACT_APP_API_BASE_URL || "").trim();
            const canTryFallback =
                process.env.NODE_ENV === "development" &&
                !explicit &&
                !apiBase &&
                endpoint !== fallbackEndpoint;

            if (canTryFallback) {
                try {
                    res = await fetch(fallbackEndpoint, payload);
                } catch {
                    const reason = e?.message ? ` (${e.message})` : "";
                    throw new Error(
                        `Core GraphQL network error${reason}. Endpoint: ${endpoint}. ` +
                            `Set REACT_APP_CORE_GRAPHQL_URL or make sure backend is reachable.`,
                    );
                }
            } else {
                const reason = e?.message ? ` (${e.message})` : "";
                throw new Error(
                    `Core GraphQL network error${reason}. Endpoint: ${endpoint}. ` +
                        `Set REACT_APP_CORE_GRAPHQL_URL or make sure backend is reachable.`,
                );
            }
        }
        let json;
        try {
            json = await res.json();
        } catch {
            throw new Error(`Core GraphQL: invalid JSON (HTTP ${res.status})`);
        }
        if (!res.ok) {
            throw new Error(json?.errors?.[0]?.message || `HTTP ${res.status}`);
        }
        if (json.errors && json.errors.length) {
            throw new Error(json.errors.map((e) => e.message).join("; "));
        }
        return json.data;
    })();

    if (!isGraphqlMutation(query)) {
        inFlightGraphqlRequests.set(dedupeKey, requestPromise);
        requestPromise.finally(() => {
            inFlightGraphqlRequests.delete(dedupeKey);
        });
    }

    return requestPromise;
}

/**
 * Multipart GraphQL (GraphQL multipart request spec) for variables that include ``File`` values.
 * Do not set ``Content-Type`` manually; the browser sets the multipart boundary.
 *
 * @param {string} query
 * @param {Record<string, unknown>} variables
 * @param {string|null|undefined} [token]
 * @param {RequestInit} [init]
 * @returns {Promise<Record<string, unknown>>}
 */
export async function coreGraphqlUpload(query, variables = {}, token, init = {}) {
    const auth = getCoreAuthToken(token);
    const initObj = init && typeof init === "object" ? { ...init } : {};
    const initHeadersRaw = initObj.headers;
    delete initObj.headers;
    delete initObj.body;

    const headers = new Headers();
    if (initHeadersRaw) {
        const h = new Headers(initHeadersRaw);
        h.forEach((value, key) => {
            if (key.toLowerCase() !== "content-type") {
                headers.set(key, value);
            }
        });
    }
    if (auth) {
        headers.set("Authorization", `Token ${auth}`);
    }

    const raw = { ...(variables && typeof variables === "object" ? variables : {}) };
    const opsVars = { ...raw };
    const map = {};
    /** @type {Array<{ path: string, file: File|Blob }>} */
    const fileEntries = [];

    Object.keys(opsVars).forEach((key) => {
        const value = opsVars[key];
        if (Array.isArray(value)) {
            const arr = [...value];
            let changed = false;
            arr.forEach((item, idx) => {
                if (item instanceof File || item instanceof Blob) {
                    fileEntries.push({ path: `variables.${key}.${idx}`, file: item });
                    arr[idx] = null;
                    changed = true;
                }
            });
            if (changed) {
                opsVars[key] = arr;
            }
        } else if (value instanceof File || value instanceof Blob) {
            fileEntries.push({ path: `variables.${key}`, file: value });
            opsVars[key] = null;
        }
    });

    fileEntries.forEach((entry, i) => {
        map[String(i)] = [entry.path];
    });

    const formData = new FormData();
    formData.append("operations", JSON.stringify({ query, variables: opsVars }));
    formData.append("map", JSON.stringify(map));

    fileEntries.forEach((entry, i) => {
        formData.append(String(i), entry.file);
    });

    const res = await fetch(getCoreGraphqlEndpoint(), {
        ...initObj,
        method: "POST",
        headers,
        body: formData,
    });
    let json;
    try {
        json = await res.json();
    } catch {
        throw new Error(`Core GraphQL upload: invalid JSON (HTTP ${res.status})`);
    }
    if (!res.ok) {
        throw new Error(json?.errors?.[0]?.message || `HTTP ${res.status}`);
    }
    if (json.errors && json.errors.length) {
        throw new Error(json.errors.map((e) => e.message).join("; "));
    }
    return json.data;
}

/**
 * Multipart GraphQL (single file), same protocol as `websiteGraphql.js`.
 *
 * @param {string} query
 * @param {Record<string, unknown>} variables include `[uploadName]: null` for the file slot
 * @param {File|Blob} file
 * @param {string|null|undefined} [token]
 * @param {string} [fileMapKey]
 * @param {string} [uploadVariableName]
 */
export async function coreGraphqlMultipartRequest(
    query,
    variables,
    file,
    token,
    fileMapKey = "0",
    uploadVariableName = "commentFile",
) {
    const auth = getCoreAuthToken(token);
    const headers = {};
    if (auth) {
        headers.Authorization = `Token ${auth}`;
    }
    const vs = { ...variables, [uploadVariableName]: null };
    const operations = JSON.stringify({ query, variables: vs });
    const map = JSON.stringify({
        [fileMapKey]: [`variables.${uploadVariableName}`],
    });
    const fd = new FormData();
    fd.append("operations", operations);
    fd.append("map", map);
    fd.append(fileMapKey, file);

    const res = await fetch(getCoreGraphqlEndpoint(), {
        method: "POST",
        headers,
        body: fd,
    });
    let json;
    try {
        json = await res.json();
    } catch {
        throw new Error(`Core GraphQL multipart: invalid JSON (HTTP ${res.status})`);
    }
    if (!res.ok) {
        throw new Error(json?.errors?.[0]?.message || `HTTP ${res.status}`);
    }
    if (json.errors && json.errors.length) {
        throw new Error(json.errors.map((e) => e.message).join("; "));
    }
    return json.data;
}

/**
 * Multipart post create with `variables.postFiles: [Upload]` mapped to `0`, `1`, …
 *
 * @param {string} query
 * @param {Record<string, unknown>} variables
 * @param {File[]} files
 * @param {string|null|undefined} [token]
 */
export async function coreGraphqlMultipartPostCreate(query, variables, files, token) {
    const auth = getCoreAuthToken(token);
    const headers = {};
    if (auth) {
        headers.Authorization = `Token ${auth}`;
    }
    const vs = {
        ...variables,
        postFiles: files.map(() => null),
    };
    const mapObj = {};
    files.forEach((file, i) => {
        mapObj[String(i)] = [`variables.postFiles.${i}`];
    });
    const operations = JSON.stringify({ query, variables: vs });
    const map = JSON.stringify(mapObj);
    const fd = new FormData();
    fd.append("operations", operations);
    fd.append("map", map);
    files.forEach((file, i) => {
        fd.append(String(i), file);
    });

    const res = await fetch(getCoreGraphqlEndpoint(), {
        method: "POST",
        headers,
        body: fd,
    });
    let json;
    try {
        json = await res.json();
    } catch {
        throw new Error(`Core GraphQL multipart post: invalid JSON (HTTP ${res.status})`);
    }
    if (!res.ok) {
        throw new Error(json?.errors?.[0]?.message || `HTTP ${res.status}`);
    }
    if (json.errors && json.errors.length) {
        throw new Error(json.errors.map((e) => e.message).join("; "));
    }
    return json.data;
}
